import { useState } from "react";
import { Search } from "lucide-react";
import { Button, Input, Label } from "@bookdental/ui";
import { BRAZILIAN_STATES } from "@bookdental/schemas";
import { fetchAddressFromCep, formatCep } from "@/lib/viacep";

export type AddressFormValues = {
  zipCode: string;
  address: string;
  addressNumber: string;
  addressComplement: string;
  neighborhood: string;
  city: string;
  state: string;
};

export const EMPTY_ADDRESS: AddressFormValues = {
  zipCode: "",
  address: "",
  addressNumber: "",
  addressComplement: "",
  neighborhood: "",
  city: "",
  state: "",
};

type Props = {
  values: AddressFormValues;
  onChange: (partial: Partial<AddressFormValues>) => void;
  disabled?: boolean;
};

const labelCls = "text-[11px] font-medium uppercase tracking-wider text-zinc-500";
const inputCls = "h-10";

export function AddressFormFields({ values, onChange, disabled }: Props) {
  const [searching, setSearching] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const handleCepSearch = async () => {
    setNotFound(false);
    const digits = values.zipCode.replace(/\D/g, "");
    if (digits.length !== 8) return;
    setSearching(true);
    const result = await fetchAddressFromCep(digits);
    setSearching(false);
    if (!result) {
      setNotFound(true);
      return;
    }
    onChange({
      zipCode: result.zipCode,
      address: result.address || values.address,
      neighborhood: result.neighborhood || values.neighborhood,
      city: result.city || values.city,
      state: result.state || values.state,
    });
  };

  return (
    <div className={disabled ? "pointer-events-none opacity-50" : ""}>
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label className={labelCls}>CEP</Label>
          <div className="flex gap-2">
            <Input
              placeholder="01310-100"
              inputMode="numeric"
              value={formatCep(values.zipCode)}
              onChange={(e) => {
                setNotFound(false);
                onChange({ zipCode: e.target.value.replace(/\D/g, "").slice(0, 8) });
              }}
              onBlur={handleCepSearch}
              className={inputCls}
              disabled={disabled}
            />
            <Button
              type="button"
              variant="outline"
              onClick={handleCepSearch}
              disabled={disabled || searching || values.zipCode.replace(/\D/g, "").length !== 8}
              className="h-10"
            >
              <Search className="h-4 w-4" />
              {searching ? "..." : "Buscar"}
            </Button>
          </div>
          {notFound && <p className="text-[11px] text-amber-700">CEP nao encontrado. Preencha manualmente.</p>}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_110px]">
          <div className="space-y-1.5">
            <Label className={labelCls}>Logradouro</Label>
            <Input
              placeholder="Rua / Avenida / ..."
              value={values.address}
              onChange={(e) => onChange({ address: e.target.value })}
              className={inputCls}
              disabled={disabled}
            />
          </div>
          <div className="space-y-1.5">
            <Label className={labelCls}>Numero</Label>
            <Input
              placeholder="123"
              value={values.addressNumber}
              onChange={(e) => onChange({ addressNumber: e.target.value })}
              className={inputCls}
              disabled={disabled}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className={labelCls}>Complemento</Label>
          <Input
            placeholder="Sala / Bloco"
            value={values.addressComplement}
            onChange={(e) => onChange({ addressComplement: e.target.value })}
            className={inputCls}
            disabled={disabled}
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className={labelCls}>Bairro</Label>
            <Input
              value={values.neighborhood}
              onChange={(e) => onChange({ neighborhood: e.target.value })}
              className={inputCls}
              disabled={disabled}
            />
          </div>
          <div className="space-y-1.5">
            <Label className={labelCls}>Cidade</Label>
            <Input
              value={values.city}
              onChange={(e) => onChange({ city: e.target.value })}
              className={inputCls}
              disabled={disabled}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className={labelCls}>Estado (UF)</Label>
          <select
            value={values.state}
            onChange={(e) => onChange({ state: e.target.value })}
            className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-1 text-sm shadow-sm disabled:opacity-50"
            disabled={disabled}
          >
            <option value="">—</option>
            {BRAZILIAN_STATES.map((uf) => (
              <option key={uf} value={uf}>
                {uf}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

/** Endereço obrigatório de organização: tudo, menos o complemento (mesma regra de
 *  `requiredAddressSchema`). */
export function enderecoCompleto(a: AddressFormValues) {
  return (
    a.zipCode.replace(/\D/g, "").length === 8 &&
    a.address.trim().length >= 2 &&
    a.addressNumber.trim().length >= 1 &&
    a.neighborhood.trim().length >= 2 &&
    a.city.trim().length >= 2 &&
    !!a.state
  );
}

/** O endereço no formato que `organization.updateMainAddress` e `me.completeProfile` recebem. */
export function enderecoParaSalvar(a: AddressFormValues) {
  return {
    zipCode: a.zipCode.replace(/\D/g, ""),
    address: a.address.trim(),
    addressNumber: a.addressNumber.trim(),
    addressComplement: a.addressComplement.trim() || undefined,
    neighborhood: a.neighborhood.trim(),
    city: a.city.trim(),
    state: a.state as never,
  };
}
